
import { logger } from "@/services/LoggingService";
import { StorageError } from "./errors";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeFileName } from "./validation";
import { STORAGE_BUCKET, CACHE_SETTINGS } from "./constants";

const COMPONENT = 'FileUpload';

export async function uploadStorageFile(
  basePath: string,
  fileName: string, 
  file: File,
  options: { 
    upsert?: boolean; 
    cacheControl?: keyof typeof CACHE_SETTINGS | string;
    contentType?: string;
  } = {}
): Promise<string> {
  try {
    logger.info(COMPONENT, 'Starting file upload', {
      basePath,
      fileName,
      fileType: file.type,
      fileSize: file.size
    });

    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const sanitizedName = sanitizeFileName(fileName);
    const fullPath = `${basePath}/${sanitizedName}.${fileExt}`;

    logger.debug(COMPONENT, 'Uploading to path', { fullPath });

    // If upsert is true, clean up existing files first
    if (options.upsert) {
      await cleanupStorageFolder(basePath);
    }

    const { error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(fullPath, file, {
        upsert: options.upsert ?? false,
        contentType: options.contentType || file.type,
        cacheControl: CACHE_SETTINGS[options.cacheControl as keyof typeof CACHE_SETTINGS] || options.cacheControl || CACHE_SETTINGS.SHORT
      });

    if (uploadError) {
      logger.error(COMPONENT, 'Upload failed', {
        error: uploadError,
        path: fullPath
      });
      throw new StorageError(
        uploadError.message,
        'UPLOAD_FAILED',
        { path: fullPath, details: uploadError }
      );
    }

    const { data: { publicUrl } } = supabase.storage
      .from(STORAGE_BUCKET)
      .getPublicUrl(fullPath);

    logger.info(COMPONENT, 'Upload successful', {
      path: fullPath,
      publicUrl
    });

    return publicUrl;
  } catch (error: any) {
    logger.error(COMPONENT, 'Upload operation failed', { 
      error: error.message,
      details: error,
      operation: 'upload'
    });
    
    if (error instanceof StorageError) {
      throw error;
    }
    
    throw new StorageError(
      'Failed to upload file',
      'UPLOAD_ERROR',
      { originalError: error }
    );
  }
}

export async function cleanupStorageFolder(folderPath: string): Promise<void> {
  try {
    logger.info(COMPONENT, 'Cleaning up folder', { folderPath });

    const { data: existingFiles, error: listError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .list(folderPath);

    if (listError) {
      throw new StorageError(
        'Failed to list files',
        'LIST_ERROR',
        { path: folderPath, details: listError }
      );
    }

    if (existingFiles && (existingFiles?.length || 0) > 0) {
      const filePaths = existingFiles.map(file => `${folderPath}/${file.name}`);
      
      logger.debug(COMPONENT, 'Removing files', {
        folderPath,
        fileCount: filePaths.length,
        files: filePaths
      });

      const { error: deleteError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .remove(filePaths);

      if (deleteError) {
        throw new StorageError(
          'Failed to delete files',
          'DELETE_ERROR',
          { paths: filePaths, details: deleteError }
        );
      }
    }
  } catch (error: any) {
    logger.error(COMPONENT, 'Cleanup operation failed', { 
      error: error.message,
      details: error,
      operation: 'cleanup',
      folderPath
    });
    
    if (error instanceof StorageError) {
      throw error;
    }
    
    throw new StorageError(
      'Failed to cleanup storage folder',
      'CLEANUP_ERROR',
      { originalError: error }
    );
  }
}
